import { useEffect, useState } from 'react'
import { apiGet } from '../../Api_Functions'

/**
 * Whether the answer of a cluster switch says it is on. Sun answers `{ enabled: boolean }` and
 * states it plainly: "a client can always trust that 'enabled false' means the action must not be
 * offered". So nothing but the boolean `true` counts as enabled -- an empty body, a missing field
 * or a stringly-typed value is OFF.
 *
 * @param {Object} setting Answer of a switch endpoint
 * @returns {boolean} True only when the switch is explicitly on
 */
export const isSwitchOn = (setting) => setting?.enabled === true

/**
 * Reads ONE cluster switch of Sun, which are all the same shape: a route answering
 * `{ enabled: boolean }`, readable by any authenticated caller and writable only with the
 * `SETTINGS` authority.
 *
 * Two guarantees, both of them load-bearing and both of them why this is shared rather than copied:
 *   1. **Default OFF.** Absence, a failed read (an older backend without the endpoint) or a body
 *      that does not say `enabled: true` leave the feature off, so upgrading changes nothing in an
 *      installation that has not opted in. Sun makes the same promise on its side: every one of
 *      these settings is seeded to `false` and an unreadable row resolves to disabled.
 *   2. **No request out of a cluster.** The switch is only asked for when the installation belongs
 *      to one, so an installation outside a cluster issues exactly the same requests as before the
 *      feature existed, with no trace of it in the network tab.
 *
 * @param {boolean} inCluster Whether this installation belongs to a cluster (`frontEndConfig`)
 * @param {string} endpoint Route of the switch, under `/endpoints/`
 * @returns {boolean} Whether the switch is on
 */
const useClusterSwitchSetting = (inCluster, endpoint) => {
  const [enabled, setEnabled] = useState(false)

  useEffect(() => {
    if (!inCluster) {
      setEnabled(false)
      return
    }
    let subscribed = true
    const loadSetting = async () => {
      try {
        const setting = await apiGet(endpoint)
        if (subscribed) {
          setEnabled(isSwitchOn(setting))
        }
      } catch (error) {
        // An unreachable switch is a switch that is off, and it must never break the window that
        // asked for it.
        if (subscribed) {
          setEnabled(false)
        }
      }
    }
    loadSetting()
    return () => {
      subscribed = false
    }
  }, [inCluster, endpoint])

  return enabled
}

export default useClusterSwitchSetting
